import {
  TextField,
  SelectField,
  NumberInput,
  DateTimeInput,
  SelectArrayInput,
  useRecordContext,
} from "react-admin";

import get from "lodash/get";
import BusinessIcon from "@mui/icons-material/Business";
import { JsonField } from "../ra-lb-tools/components/jsonField";
import { MyList } from "../ra-lb-tools/components/myList";
import { UserField } from "./users";
import { MyDateField } from "../ra-lb-tools/components/myDateField";
import { LockerField } from "../components/referenceFields";
import { addTimeDiff, LogLink } from "./towerHistory";
import { TowerField, TowerInput } from "./tower/towers";
import { Box } from "@mui/material";
import { LockersStatusField } from "./lockers";
import { Person, PersonOff } from "@mui/icons-material";
import { FlexBox } from "../ra-lb-tools/components/flexBox";
import { LockerUsageTypeField } from "./billing/lockerUsage";
import { LockerStatusInput } from "./lockerStatus";

export const SystemIcon = BusinessIcon;

/**
 * TODO:
 * Check for enhancement
 * Currently this needs to be manually synchronized from
 * hal-backend/app/app/models/locker_history.py::class LockerHistoryType(EnumBase)
 */
const typeChoices = {
  StatusChange: { id: 1, name: "Status Change" },
  NameChange: { id: 2, name: "Name Change" },
  Assigned: { id: 3, name: "Assigned" },
  Released: { id: 4, name: "Released" },
  Reassigned: { id: 5, name: "Reassigned" },
  UsageTypeChange: { id: 6, name: "Usage Type Change" },
};

const lockerHistoryFilters = (typeChoices) => [
  <NumberInput source="lockerId" alwaysOn key={1} label="Locker Id" />,
  <TowerInput source="tower_id" alwaysOn key="tower_id" />,
  <SelectArrayInput
    label="Type"
    source="type"
    choices={typeChoices}
    alwaysOn
    key={"history##type"}
  />,
  <DateTimeInput
    label="From"
    source="timestamp_ge"
    alwaysOn
    key={"timestamp_ge"}
  />,
  <DateTimeInput
    label="To"
    source="timestamp_le"
    alwaysOn
    key={"timestamp_le"}
  />,
  <LockerStatusInput
    label="To Status"
    source="data.new"
    alwaysOn
    key={"data.new"}
  />,
];

const DetailField = ({ record }) => {
  record = record || useRecordContext();

  let detail = null;

  switch (record.type) {
    case typeChoices.StatusChange.id:
      detail = (
        <FlexBox className="StatusChangeDetail">
          <span>Status changed from </span>
          <LockersStatusField statusIdField="data.old" showColors={true} />
          <span>to</span>
          <LockersStatusField statusIdField="data.new" showColors={true} />
        </FlexBox>
      );
      break;
    case typeChoices.NameChange.id:
      detail = (
        <FlexBox className="NameChangeDetail">
          Name changed from{" "}
          <span style={{ fontWeight: "bold" }}>
            &apos;
            {get(record, "data.old")}&apos;
          </span>
          <span>to</span>
          <span style={{ fontWeight: "bold" }}>
            &apos;
            {get(record, "data.new")}&apos;
          </span>
        </FlexBox>
      );
      break;
    case typeChoices.Assigned.id:
      detail = (
        <FlexBox className="AssignedDetail">
          <Person color="info" />
          <Box>Assigned to</Box>
          <UserField source="data.new" />
        </FlexBox>
      );
      break;
    case typeChoices.Released.id:
      detail = (
        <FlexBox className="ReleasedDetail">
          <PersonOff color="error" />
          <Box>Released from</Box>
          <UserField source="data.old" />
        </FlexBox>
      );
      break;
    case typeChoices.Reassigned.id:
      detail = (
        <FlexBox className="ReassignedDetail" sx={{ ">*": { flexShrink: 1 } }}>
          <Box>Reassigned from</Box>
          <FlexBox className="no-wrap" sx={{ alignItems: "end" }}>
            <PersonOff color="error" />
            <UserField source="data.old" />
          </FlexBox>
          <span>to</span>
          <FlexBox className="no-wrap" sx={{ alignItems: "end" }}>
            <Person color="info" />
            <UserField source="data.new" />
          </FlexBox>
        </FlexBox>
      );
      break;
    case typeChoices.UsageTypeChange.id:
      detail = (
        <FlexBox
          className="UsageTypeChangeDetail"
          sx={{ ">*": { flexShrink: 1 } }}
        >
          <Box>Usage type changed from</Box>
          <LockerUsageTypeField source="data.old_data.type" />
          <span>to</span>
          <LockerUsageTypeField source="data.update_data.type" />
        </FlexBox>
      );
      break;

    default:
      break;
  }

  return <FlexBox className="TowerHistoryDetail">{detail}</FlexBox>;
};

export const LockerHistoryList = (props) => {
  const title = "Locker History";
  const resource = "locker-history";
  const typeChoicesList = Object.values(typeChoices);

  return (
    <MyList
      title={title}
      resource={resource}
      filters={lockerHistoryFilters(typeChoicesList)}
      sort={{
        field: "id",
        order: "DESC",
      }}
      forceShow={true}
      queryOptions={{
        meta: {
          transformResponse: addTimeDiff,
        },
      }}
      dataGridProps={{
        omit: ["_time_diff"],
      }}
      {...props}
    >
      <TextField source="id" />
      <LockerField source="lockerId" />
      <MyDateField source="timestamp" showTime />
      <TextField source="_time_diff" />
      <SelectField source="type" choices={typeChoicesList} />
      <DetailField source="detail" sortable={false} />
      <JsonField source="formattedData" />
      <UserField source="formattedData.user_id" label="User" sortable={false} />
      <TowerField source="towerId" sortable={false} />
      <LogLink source="Log" />
    </MyList>
  );
};
